import { Confetti } from '@components/App/Modal/PlanUpdated/Confetti'
import { OverlayPortal } from '@lib/components'
import IconX from '@lib/icons/IconX'
import { ThemeColors } from '@octopus-do/octopus-storybook'
import { migrationUiSelector } from '@store/migrationSelectors/uiSelectors'
import { closeModal, openModal } from '@storeOld/reducers/ui.js'
import cls from 'classnames'
import { useEffect, useRef } from 'react'
import { connect, useSelector } from 'react-redux'

import './Modal.css'

import { noop } from '@/helpers'
import {
  MODAL_AUTH_SETUP_ACCOUNT,
  MODAL_NONE,
  MODAL_PLAN_UPGRADE_SUCCESSFUL,
  MODAL_SUBSCRIBE,
  MODAL_TEAM_UPGRADE_SUCCESSFUL,
} from './constants'
import ModalTypes from './modalTypes'

const Modal = ({
  className,
  onKeydown,
  onLockedEscape,
  onClick,
  isDialogActive,
  children,
}) => {
  const modalRef = useRef(null)
  const dialogRef = useRef(null)
  // Read through a ref: the keydown listener is bound once per dialog state, and the
  // handler it needs belongs to whatever modal is open by the time Esc is pressed.
  const lockedEscapeRef = useRef(onLockedEscape)
  lockedEscapeRef.current = onLockedEscape

  const modalType = useSelector(migrationUiSelector.getModalType)
  const modalProps = useSelector(migrationUiSelector.getModalProps)

  useEffect(() => {
    const disabledClose = [MODAL_AUTH_SETUP_ACCOUNT].includes(modalType)

    function onDocumentKeydown(e) {
      if (e.which === 27 && !disabledClose) {
        if (
          !isDialogActive &&
          !document.querySelector('.editor-image_preview')
        ) {
          // A locked modal doesn't close on Esc: Esc asks the same question its × does,
          // or does nothing when there is no question to ask.
          if (modalProps.isLocked) {
            lockedEscapeRef.current()
            return
          }

          dialogRef.current?.classList.remove('show')

          setTimeout(() => {
            modalRef.current?.classList.remove('show')

            setTimeout(() => {
              onKeydown()
            }, 175)
          }, 125)
        }
      }
    }

    modalRef.current?.classList.add('show')

    setTimeout(() => {
      dialogRef.current?.classList.add('show')
    }, 50)

    document.addEventListener('keydown', onDocumentKeydown)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onDocumentKeydown)
      document.body.style.overflow = 'auto'
    }
  }, [isDialogActive, modalProps.isLocked])

  useEffect(() => {
    if (modalProps.scrolless) {
      modalRef.current?.classList.add('Modal-scrolless')
    }
  }, [])

  function onModalClick({ currentTarget, target }) {
    if (currentTarget === target) {
      onClick()
    }
  }

  return (
    <div
      className={cls('Modal', ThemeColors.light, className)}
      onMouseDown={onModalClick}
      ref={modalRef}
    >
      <div className="Modal-dialog" children={children} ref={dialogRef} />
      {[MODAL_PLAN_UPGRADE_SUCCESSFUL, MODAL_TEAM_UPGRADE_SUCCESSFUL].includes(
        modalType
      ) && <Confetti />}
    </div>
  )
}

const Close = ({ onClick }) => {
  const onClose = () => {
    document.querySelector('.Modal-dialog')?.classList.remove('show')

    setTimeout(() => {
      document.querySelector('.Modal')?.classList.remove('show')

      setTimeout(() => {
        onClick()
      }, 175)
    }, 125)
  }

  return (
    <div className="Modal-close" onClick={onClose}>
      <IconX />
    </div>
  )
}

function render(type) {
  switch (type) {
    case MODAL_NONE:
      return null
    default:
      return ModalTypes[type] || null
  }
}

const getModalFeatures = (modalType) => {
  switch (modalType) {
    case MODAL_AUTH_SETUP_ACCOUNT:
    case MODAL_SUBSCRIBE:
      return []
    default:
      return ['keyboard', 'backdrop']
  }
}

const CloseButton = ({ features, onClick }) => {
  if (!features.includes('keyboard')) {
    return null
  }

  return <Close onClick={onClick} />
}

const Container = ({
  modalType,
  modalProps,
  closeModal,
  openModal,
  dialogType,
}) => {
  const Component = render(modalType, { openModal })

  if (!Component) {
    return null
  }

  const features = getModalFeatures(modalType)
  const disabledClose = [MODAL_AUTH_SETUP_ACCOUNT, MODAL_SUBSCRIBE].includes(
    modalType
  )

  const close = () => {
    if (window.octRequest) {
      window.octRequest.xhr.abort()
    }

    closeModal()
  }

  const closeOnClick = () => {
    if (disabledClose) {
      return
    }

    if (modalProps.confirmClosing) {
      modalProps
        .confirmClosing()
        .then((confirmed) => confirmed && close())
        .catch(noop)
    } else {
      close()
    }
  }

  const animateClose = (callback) => () => {
    const dialog = document.querySelector('.Modal-dialog')
    const modal = document.querySelector('.Modal')

    dialog?.classList.remove('show')
    setTimeout(() => {
      modal?.classList.remove('show')
      setTimeout(() => callback(), 175)
    }, 125)
  }
  const onModalKeydown = features.includes('keyboard')
    ? animateClose(closeModal)
    : noop
  const onModalClick = features.includes('backdrop')
    ? animateClose(closeModal)
    : noop

  const onClickAway = () => {
    if (modalProps.isLocked) {
      return false
    }

    document.querySelector('.Modal-dialog')?.classList.remove('show')

    setTimeout(() => {
      document.querySelector('.Modal')?.classList.remove('show')

      setTimeout(() => {
        if (window.octRequest) {
          window.octRequest.xhr.abort()
        }

        onModalClick()
      }, 175)
    }, 125)
  }

  return (
    <OverlayPortal root="modals">
      <Modal
        onKeydown={onModalKeydown}
        onLockedEscape={modalProps.confirmClosing ? closeOnClick : noop}
        onClick={onClickAway}
        isDialogActive={dialogType !== MODAL_NONE}
        className={modalProps.modalClassName}
      >
        <Component
          openModal={openModal}
          closeModal={closeModal}
          onClose={closeOnClick}
          closeButton={
            <CloseButton onClick={closeOnClick} features={features} />
          }
          {...modalProps}
        />
      </Modal>
    </OverlayPortal>
  )
}

const mapStateToProps = ({ ui }) => ({
  modalType: ui.modalType,
  modalProps: ui.modalProps,
  dialogType: ui.dialogType,
})

const mapDispatchToProps = {
  openModal,
  closeModal,
}

export default connect(mapStateToProps, mapDispatchToProps)(Container)
